import type {GridUserImageItemStorage} from '@Components/base-grid/components/grid-user-image-item';
import type {GridUserVideoItemStorage} from '@Components/base-grid/components/grid-user-video-item';
import type {GridUserAudioItemStorage} from '@Components/base-grid/components/grid-user-audio-item';
import {UserVideoSource} from '@Libraries/user-video-library';
import type {UserVideoVOResponse} from '@Libraries/user-video-library';
import type {RGBA} from '@Utils/color.util';
import type {UserAudioSource, UserAudioVOResponse} from '@Libraries/user-audio-library';
import type {LayoutData, ScheduleDataType} from '@PosterWhiteboard/items/table-item/table-item.types';
import type {Cell} from '@PosterWhiteboard/items/layouts/cells/cell';
import type {ImageItemSourceType} from '@Libraries/image-item.library';
import {ImageItemSource} from '@Libraries/image-item.library';
import type {GettyAssetClassification} from '@PosterWhiteboard/poster/poster.types';
import {GettyAssetType} from '@PosterWhiteboard/poster/poster.types';
import type {MorphType} from '@Libraries/fancy-text-library';
import type {AnimatedSprite} from '@Libraries/animated-sprite.library';
import type {LayoutTypes} from '@PosterWhiteboard/items/layouts/layout.types';
import type {TranscriptItemObject} from '@PosterWhiteboard/items/transcript-item/transcript-item.types';
import type {GridStockImageItemStorage} from '@Components/base-grid/components/grid-stock-image-item';
import type {SubtitleStylesAndAnimation} from '@PosterWhiteboard/items/transcript-item/subtitle/subtitle.types';
import {PMW_STOCK_IMAGE_EXTENSION, PMWStockImageSource} from '@Libraries/pmw-stock-media-library.types';
import type {StickerItemSourceType} from '@Libraries/pmw-stock-media-library.types';
import type {UserImageVOResponse} from './user-image-library';
import {getHashedFilenameForStockAsset} from './user-image-library';
import type {DeepPartial} from '@/global';
import type {LineCapStyle, LineDashType} from '@PosterWhiteboard/items/line-item/line-item.types';
import type {CellType} from '@PosterWhiteboard/items/layouts/cells/cell.types';

export type ElementData =
  | FancyTextData
  | TextData
  | ImageData
  | TempImageData
  | AiChatImagePlaceholderData
  | VideoData
  | PMWShapeData
  | PMWIconData
  | AnimatedStickerData
  | AiAnimatedStickerData
  | AudioData
  | QRCodeData
  | LineData
  | VectorData
  | TabsData
  | TableData
  | ScheduleLayoutData
  | MenuData
  | SlideshowData
  | PMWExtractedGettyStickerData
  | PMWStillStickerData
  | PMWAiStickerData
  | PMWAiIconData
  | TranscriptData;

export type ElementDataWithUid = ElementData & {uid: string};
export type StickerData = AnimatedStickerData | PMWStillStickerData | PMWExtractedGettyStickerData;

export enum ElementDataType {
  IMAGE = 'image',
  QR_CODE = 'qrcode',
  LINE = 'line',
  ANIMATED_STICKER = 'animated_sticker',
  AI_ANIMATED_STICKER = 'ai_animated_sticker',
  PMW_SHAPE = 'pmw_shape',
  VIDEO = 'video',
  TEXT = 'text',
  FANCY_TEXT = 'fancytext',
  AUDIO = 'audio',
  VECTOR = 'vector',
  TABLE = 'table',
  MENU = 'menu',
  TAB = 'tabs',
  SLIDESHOW = 'slideshow',
  ICON = 'icon',
  EXTRACTED_GETTY_STICKER = 'extracted_getty_sticker',
  AI_ICON = 'ai_icon',
  AI_STICKER = 'ai_sticker',
  STILL_STICKER = 'still_sticker',
  TRANSCRIPT = 'transcript',
}

export interface SlideshowData extends ElementDataBase {
  type: ElementDataType.SLIDESHOW;
}

export interface TableData extends ElementDataBase {
  type: ElementDataType.TABLE;
  columns: number;
  rows: number;
  tableData: string[][];
  highlightedRows: number[];
}

export interface ScheduleLayoutData extends ElementDataBase {
  type: ElementDataType.TABLE;
  columns: number;
  rows: number;
  tableData: ScheduleDataType[][];
  highlightedRows: number[];
  columnsData: CellType[];
  layoutStyle: LayoutData;
  unusedData: ScheduleDataType[][];
}

export interface MenuData extends ElementDataBase {
  type: ElementDataType.MENU;
  layoutDataMap: Record<string, Cell[]>;
  rows: number;
  columns: number;
  itemIds: string[];
  layoutStyle: LayoutTypes;
}

export interface TabsData extends ElementDataBase {
  type: ElementDataType.TAB;
  text?: string;
}

export interface QRCodeData extends ElementDataBase {
  type: ElementDataType.QR_CODE;
}

export interface LineData extends ElementDataBase {
  type: ElementDataType.LINE;
  fill: RGBA;
  lineDashType: LineDashType;
  lineCapStart: LineCapStyle;
  lineCapEnd: LineCapStyle;
}

export interface FancyTextData extends ElementDataBase {
  type: ElementDataType.FANCY_TEXT;
  text: string;
  idFancyText: string;
  effect: string;
  colors: RGBA[];
  fontFamily: string;
  morphType: MorphType;
  morphAmount: number;
}

export interface TextData extends ElementDataBase {
  type: ElementDataType.TEXT;
  text?: string;
}

export interface AddTranscriptOpts {
  optionsToAdd?: DeepPartial<TranscriptItemObject>;
  selectOnAdd?: boolean;
}

export interface TranscriptData extends ElementDataBase {
  type: ElementDataType.TRANSCRIPT;
  subtitles: AddSubtitleData[];
  areAIGenerated: boolean;
  styles?: SubtitleStylesAndAnimation;
  selectedSubtitleTemplateId?: string;
}

export interface AddWordData {
  id: string;
  startTime: number;
  endTime: number;
  text: string;
}

export interface AddSubtitleData {
  id: string;
  startTime: number;
  endTime: number;
  text: string;
  words: AddWordData[];
}

export interface BaseImageData extends MediaDimensions {
  hashedFilename: string;
  extension: string;
  hasTransparency: boolean;
}

export interface ImageData extends ElementDataBase, BaseImageData {
  type: ElementDataType.IMAGE;
  source: ImageItemSourceType;
  uploaderId: number;
  replaceId?: string;
}

export interface AiImageDataForDataUrlImage extends Pick<ImageData, 'type' | 'source'> {}
export interface StockImageDataForDataUrlImage extends Pick<ImageData, 'type' | 'hashedFilename' | 'source' | 'extension'> {}

export interface TempImageData extends ElementDataBase, MediaDimensions {
  type: ElementDataType.IMAGE;
  source: ImageItemSourceType;
  uid: string;
  dataUrl: string;
  stockId?: string;
}

export interface AiChatImagePlaceholderData extends TempImageData {
  aiChatImageAssetHashedId: string;
}

export interface DeferredImageUploadData {
  type: ElementDataType.IMAGE;
  isDeferredEditorUpload: true;
  file: File;
  width: number;
  height: number;
}

export type AddMediaItem = ElementData | DeferredImageUploadData;

interface MediaDimensions {
  width?: number;
  height?: number;
}

export interface AnimatedStickerData extends ElementDataBase {
  type: ElementDataType.ANIMATED_STICKER;
  hashedFilename: string;
  duration: number;
  frameRate: number;
  highResAnimatedSprite: AnimatedSprite;
  screenAnimatedSprite: AnimatedSprite;
  stickerSource: StickerItemSourceType;
}

export interface AiAnimatedStickerData extends ElementDataBase {
  type: ElementDataType.AI_ANIMATED_STICKER;
  hashedFilename: string;
  duration: number;
  frameRate: number;
  highResAnimatedSprite: AnimatedSprite;
  screenAnimatedSprite: AnimatedSprite;
  stickerSource: StickerItemSourceType;
}

export interface PMWShapeData extends ElementDataBase {
  type: ElementDataType.PMW_SHAPE;
  hashedFilename: string;
}

export interface PMWIconData extends ElementDataBase {
  type: ElementDataType.ICON;
  hashedFilename: string;
}

export interface PMWAiIconData extends ElementDataBase {
  type: ElementDataType.AI_ICON;
  hashedFilename: string;
}

export interface PMWStillStickerData extends ElementDataBase {
  type: ElementDataType.STILL_STICKER;
  hashedFilename: string;
  source?: ImageItemSourceType;
}

export interface PMWAiStickerData extends ElementDataBase {
  type: ElementDataType.AI_STICKER;
  hashedFilename: string;
  source?: ImageItemSourceType;
}

export interface PMWExtractedGettyStickerData extends ElementDataBase {
  type: ElementDataType.EXTRACTED_GETTY_STICKER;
  hashedFilename: string;
  source?: ImageItemSourceType;
}

export interface VideoData extends ElementDataBase, MediaDimensions {
  type: ElementDataType.VIDEO;
  hashedFilename: string;
  extension: string;
  duration: number;
  frameRate: number;
  source: UserVideoSource;
  hasTransparency: boolean;
}

export interface AudioData extends ElementDataBase {
  type: ElementDataType.AUDIO;
  filename: string;
  extension: string;
  duration: number;
  src: UserAudioSource;
  name: string;
}

export interface VectorData extends ElementDataBase {
  type: ElementDataType.VECTOR;
  hashedFilename: string;
  name: string;
  fillColor: RGBA[];
}

export interface ElementDataBase {
  type: ElementDataType;
}

export const isTempImageData = (data: any): data is TempImageData => {
  return (data as TempImageData).type === ElementDataType.IMAGE && 'source' in data && 'uid' in data && 'dataUrl' in data;
};

export const isAiChatImagePlaceholderData = (data: unknown): data is AiChatImagePlaceholderData => {
  return isTempImageData(data) && 'aiChatImageAssetHashedId' in data;
};

export const isDeferredImageUploadData = (data: unknown): data is DeferredImageUploadData => {
  if (typeof data !== 'object' || data === null) {
    return false;
  }
  return (data as DeferredImageUploadData).type === ElementDataType.IMAGE && 'isDeferredEditorUpload' in data && 'file' in data;
};

export const isDirectlyAddableMediaData = (data: unknown): data is TempImageData | DeferredImageUploadData => {
  return isTempImageData(data) || isDeferredImageUploadData(data);
};

const getGettyIdentityKeyFromElementData = (elementData: ImageData | TempImageData): string => {
  if ('hashedFilename' in elementData) {
    return elementData.hashedFilename;
  }
  if (elementData.stockId) {
    return getHashedFilenameForStockAsset(elementData.source, elementData.stockId);
  }
  return elementData.uid;
};

export const getGettyAssetClassification = (data: ElementData): GettyAssetClassification | null => {
  if (data.type === ElementDataType.EXTRACTED_GETTY_STICKER) {
    return {gettyType: GettyAssetType.EXTRACTED_STICKER, identityKey: data.hashedFilename};
  }
  if (data.type === ElementDataType.IMAGE) {
    if (data.source === ImageItemSource.PMW_EXTRACTED_GETTY_STICKER) {
      return {gettyType: GettyAssetType.EXTRACTED_STICKER, identityKey: getGettyIdentityKeyFromElementData(data)};
    }
    if (data.source === ImageItemSource.GETTY || data.source === ImageItemSource.GETTY_ILLUSTRATIONS) {
      return {gettyType: GettyAssetType.IMAGE, identityKey: getGettyIdentityKeyFromElementData(data)};
    }
  }
  if (data.type === ElementDataType.VIDEO && data.source === UserVideoSource.GETTY) {
    return {gettyType: GettyAssetType.VIDEO, identityKey: data.hashedFilename};
  }
  return null;
};

export const getStockImageDataFromGridStockImageItemStorage = (gridData: GridStockImageItemStorage): StockImageDataForDataUrlImage => {
  return {
    type: ElementDataType.IMAGE,
    hashedFilename: getHashedFilenameForStockAsset(gridData.source, gridData.id),
    source: gridData.source,
    extension: gridData.data.type as string,
  };
};

export const getGeneratedAiImageDataFromGridItemStorage = (gridData: GridStockImageItemStorage): AiImageDataForDataUrlImage => {
  return {
    type: ElementDataType.IMAGE,
    source: gridData.source,
  };
};

export const getImageDataFromUserImageVO = (userImageVO: UserImageVOResponse): ImageData => {
  return {
    type: ElementDataType.IMAGE,
    hashedFilename: userImageVO.filename,
    source: userImageVO.src,
    extension: userImageVO.ext,
    hasTransparency: userImageVO.hasTransparency,
    uploaderId: parseInt(userImageVO.uploaderId, 10),
  };
};

export const getImageDataForPMWStockImage = (gridItem: GridStockImageItemStorage): ImageData => {
  return {
    type: ElementDataType.IMAGE,
    hashedFilename: gridItem.data.hashedFilename as string,
    source: PMWStockImageSource.PMW_STOCK_IMAGE,
    extension: PMW_STOCK_IMAGE_EXTENSION,
    hasTransparency: !!gridItem.data.hasTransparency,
    uploaderId: 0,
  };
};

export const getImageDataFromGridUserImageItemStorage = (gridItem: GridUserImageItemStorage): ImageData => {
  return {
    type: ElementDataType.IMAGE,
    hashedFilename: gridItem.hashedFilename,
    source: gridItem.source,
    extension: gridItem.extension,
    hasTransparency: gridItem.hasTransparency,
    uploaderId: gridItem.uploaderId,
    width: gridItem.width,
    height: gridItem.height,
  };
};

export const getVideoDataFromGridUserVideoItemStorage = (gridItem: GridUserVideoItemStorage): VideoData => {
  return {
    type: ElementDataType.VIDEO,
    hashedFilename: gridItem.hashedFilename,
    source: gridItem.source,
    extension: gridItem.extension,
    hasTransparency: gridItem.hasTransparency,
    duration: gridItem.duration,
    frameRate: gridItem.frameRate,
    width: gridItem.width,
    height: gridItem.height,
  };
};

export const getAudioDataFromGridUserAudioItemStorage = (gridItem: GridUserAudioItemStorage): AudioData => {
  return {
    type: ElementDataType.AUDIO,
    filename: gridItem.hashedFilename,
    src: gridItem.source,
    extension: gridItem.extension,
    duration: gridItem.duration,
    name: gridItem.name,
  };
};

export const getAudioDataFromUserAudioVO = (userAudioVo: UserAudioVOResponse): AudioData => {
  return {
    type: ElementDataType.AUDIO,
    name: userAudioVo.name,
    extension: userAudioVo.ext,
    src: userAudioVo.src,
    filename: userAudioVo.filename,
    duration: userAudioVo.duration,
  };
};

export const getVideoDataFromUserVideoVO = (userVideoVO: UserVideoVOResponse): VideoData => {
  return {
    type: ElementDataType.VIDEO,
    hashedFilename: userVideoVO.filename,
    source: userVideoVO.src,
    duration: userVideoVO.duration,
    frameRate: userVideoVO.frameRate,
    extension: userVideoVO.ext,
    hasTransparency: userVideoVO.hasTransparency,
  };
};

export const STICKER_ELEMENT_TYPES: ElementDataType[] = [
  ElementDataType.STILL_STICKER,
  ElementDataType.EXTRACTED_GETTY_STICKER,
  ElementDataType.ANIMATED_STICKER,
  ElementDataType.AI_ANIMATED_STICKER,
  ElementDataType.AI_STICKER,
];

export function isStickerElementType(type: ElementDataType): boolean {
  return STICKER_ELEMENT_TYPES.includes(type);
}
